/**
 * AssignLayoutModal — Globalne typy dokumentów, Etap 2 (layouty per typ dokumentu, T2.3).
 *
 * Okno przypisania/zmiany layoutu formularza na typie globalnym w kontekście
 * jednej firmy (nadpisanie tenantowe — `document_type_overrides.form_layout_id`).
 *
 * B.2/B.3: WIĘKSZE okno z PRZESZUKIWALNĄ LISTĄ (zamiast małego natywnego `<select>`).
 * Każdy wiersz = nazwa + znacznik „(domyślny)" + ikonka pochodzenia „systemowy"
 * TYLKO gdy layout ma wypełniony `sourceViewSlug` (layout centralny/producenta —
 * jedyny writer to materializacja widoków kartoteki). Wiersze bez znacznika NIE
 * dostają fałszywej etykiety „własny" — pusty znacznik jest niejednoznaczny
 * (bazowy „Default" z kodu vs layout zrobiony przez tenanta), więc go nie
 * etykietujemy. Filtr po nazwie jest diakrytyka-insensitive (wspólny
 * `normalizeSearch` z `lib/search-normalize`).
 *
 * Rodzic dostarcza listę layoutów FORM kartoteki wybranej klasy (`layoutsQ` —
 * ten sam co picker w `DocumentTypeFormModal`) oraz aktualnie przypisany layout
 * (`currentLayoutId`, `null` = brak nadpisania). `onConfirm` dostaje wybrane id
 * albo `null` (opcja „— brak —") — kontrakt bez zmian, rodzic woła `PUT .../override`.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import type { FormLayoutOption } from '@/components/settings/DocumentTypeFormModal';
import { normalizeSearch } from '@/lib/search-normalize';

import { OriginBadge } from './OriginBadge';

interface Props {
  open: boolean;
  layouts: FormLayoutOption[];
  currentLayoutId: string | null;
  submitting?: boolean;
  onConfirm: (formLayoutId: string | null) => void;
  onClose: () => void;
}

export function AssignLayoutModal({
  open,
  layouts,
  currentLayoutId,
  submitting,
  onConfirm,
  onClose,
}: Props) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<string>(currentLayoutId ?? '');
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (open) {
      setSelected(currentLayoutId ?? '');
      setSearch('');
    }
  }, [open, currentLayoutId]);

  const normalizedSearch = search ? normalizeSearch(search) : '';
  const filtered = useMemo(
    () =>
      normalizedSearch
        ? layouts.filter((l) => normalizeSearch(l.name).includes(normalizedSearch))
        : layouts,
    [layouts, normalizedSearch],
  );

  const rowClass = (isSelected: boolean) =>
    `flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-bg-secondary ${
      isSelected ? 'bg-accent-50 dark:bg-accent-900/20' : ''
    }`;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('tenant.documentTypes.assignLayout.title')}
      size="lg"
      footer={
        <>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={submitting}
            data-testid="assign-layout-cancel"
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={() => onConfirm(selected.trim() ? selected : null)}
            loading={submitting}
            data-testid="assign-layout-confirm"
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="mb-2 text-sm text-text-secondary">
        {t('tenant.documentTypes.assignLayout.layoutLabel')}
      </div>

      <Input
        type="search"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={t('tenant.documentTypes.assignLayout.searchPlaceholder')}
        aria-label={t('tenant.documentTypes.assignLayout.searchPlaceholder')}
        containerClassName="mb-2"
        data-testid="assign-layout-search"
      />

      <div
        role="listbox"
        aria-label={t('tenant.documentTypes.assignLayout.layoutLabel')}
        className="max-h-72 overflow-y-auto rounded-lg border border-border-primary"
      >
        {/* „— brak —" zawsze widoczne (czyści nadpisanie), niezależne od filtra. */}
        <button
          type="button"
          role="option"
          aria-selected={selected === ''}
          onClick={() => setSelected('')}
          className={`${rowClass(selected === '')} border-b border-border-primary`}
          data-testid="assign-layout-option-none"
        >
          <span className="italic text-text-secondary">
            {t('tenant.documentTypes.assignLayout.none')}
          </span>
        </button>

        {filtered.map((l) => {
          const isSelected = selected === l.id;
          return (
            <button
              key={l.id}
              type="button"
              role="option"
              aria-selected={isSelected}
              onClick={() => setSelected(l.id)}
              className={`${rowClass(isSelected)} border-b border-border-primary last:border-b-0`}
              data-testid={`assign-layout-option-${l.id}`}
            >
              <span className="min-w-0 flex-1 truncate text-text-primary">
                {l.isDefault
                  ? `${l.name} ${t('tenant.documentTypes.form.formLayoutDefaultSuffix')}`
                  : l.name}
              </span>
              {/* Ikonka „systemowy" TYLKO dla layoutów centralnych (znacznik wypełniony). */}
              {l.sourceViewSlug ? (
                <OriginBadge origin="global" data-testid={`assign-layout-origin-${l.id}`} />
              ) : null}
            </button>
          );
        })}

        {filtered.length === 0 && (
          <div className="px-3 py-2 text-sm text-text-secondary" data-testid="assign-layout-empty">
            {t('tenant.documentTypes.assignLayout.noResults')}
          </div>
        )}
      </div>
    </Modal>
  );
}
